import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { isEmpty } from 'lodash'
import { Moment } from 'moment'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import BankOfHoursPanel from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/BankOfHoursPanel'
import PayrollSetupWarning from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/PayrollSetupWarning'
import type Store from 'modules/attendance/components/ClockIn/Store'
import CompensationModalWrapper from 'modules/attendance/components/ClockIn/TabsBreakdown/CompensationModalWrapper'
import { useEmployeeSidepanelNavigation } from 'modules/attendance/hooks/useEmployeeSidepanelNavigation'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { SidePanelTab } from 'modules/attendance/lib/sidepanelTabTypes'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import BalanceCarryoverUtils from 'modules/attendance/lib/utils/BalanceCarryoverUtils'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

const sidePanelTitle = (selectedContext: SidePanelTab) => {
  switch (selectedContext) {
    case 'bank_of_hours':
      return i18n.t('clock_in.bank_of_hours.button.label')
    case 'worked_hours':
      return i18n.t('clock_in.sidepanel.time_breakdown')
    case 'compensation':
      return i18n.t('clock_in.sidepanel.tabs.compensation')
    default:
      throw new Error(`Unhandled SidePanelTab value: ${selectedContext}`)
  }
}

type Props = {
  tabFromNavigation: SidePanelTab
  timeSheetData: ReturnType<typeof useTimesheetData>
  timeSheetDate: Moment
  employeeId: number
  store: Store
}

const BankOfHoursBreakdownPanel = ({
  tabFromNavigation,
  timeSheetData,
  timeSheetDate,
  employeeId,
  store,
}: Props) => {
  const { cycle } = useAttendanceDateNavigator()
  const { employee } = useCurrentSession()
  const [openCompensationModal, setOpenCompensationModal] = React.useState(false)
  const [openPayrollWarningModal, setOpenPayrollWarningModal] = React.useState(false)

  const { employeesSettings, timeBlocksBalance, currentEmployeesSetting } = timeSheetData
  const userCheckingOwnTimesheet = employeeId === employee?.id

  // TODO: Remove this logic once the feature flag dev_compensations is removed
  const payrollCompensationEnabled =
    EmployeesSettingsUtils.hasPayrollCompensationEnabled(employeesSettings)

  const timeoffCompensationEnabled =
    EmployeesSettingsUtils.hasTimeoffCompensationEnabled(employeesSettings)

  const samePeriodCompensation = Boolean(
    BalanceCarryoverUtils.samePeriodCompensation({
      compensationPeriod: currentEmployeesSetting?.extraTimeCompensationFrequency,
      viewCycle: cycle,
      timeSheetDate,
    })
  )

  const compensationEnabled = payrollCompensationEnabled || timeoffCompensationEnabled

  const { selectedTab, closePanel } = useEmployeeSidepanelNavigation({
    tabFromNavigation,
    compensationEnabled,
    timeSheetDate,
    employeeId,
  })

  useAsyncData(
    async () =>
      policyAuthorizations.authorizePolicies('attendance.approve_period', [
        {
          id: employeeId,
          policyTargetClass: '::Employee',
        },
      ]),
    []
  )

  const { featureAvailability: hasPayroll } = useAttendanceFeatures(Features.payroll_management)

  const onCompensate = () => {
    track(trackingEvents.timeTracking.sidePanelCompensatePressed, {})
    hasPayroll ? setOpenCompensationModal(true) : setOpenPayrollWarningModal(true)
  }

  const { pendingPayrollBlocks } = timeBlocksBalance.payrollCompensationPendingAndCompensated
  const canApprovePeriod = policyAuthorizations.for('attendance.approve_period', {
    id: employeeId,
    policyTargetClass: '::Employee',
  })

  const disableCompensateButton = isEmpty(pendingPayrollBlocks)

  const showCompensateButton =
    selectedTab === 'compensation' &&
    canApprovePeriod &&
    payrollCompensationEnabled &&
    samePeriodCompensation

  const closeSidePanel = () => {
    track(trackingEvents.timeTracking.closeBankOfHours, {
      user_checking_own_timesheet: userCheckingOwnTimesheet,
    })
    closePanel()
  }

  return (
    <Sidepanel
      open
      overlay
      title={sidePanelTitle(tabFromNavigation)}
      onClose={() => closeSidePanel()}
      closeLabel={i18n.t('common.close')}
      boxPadding={false}
      footer={
        showCompensateButton && (
          <Box paddingY="s6" paddingX="s8" onClick={onCompensate}>
            <Button disabled={disableCompensateButton}>
              {i18n.t('clock_in.sidepanel.compensation.button_text')}
            </Button>
          </Box>
        )
      }
    >
      {openPayrollWarningModal && (
        <PayrollSetupWarning onCloseModal={() => setOpenPayrollWarningModal(false)} />
      )}

      <CompensationModalWrapper
        employeeId={employeeId}
        timeSheetData={timeSheetData}
        timeSheetDate={timeSheetDate}
        onClose={closePanel}
        openModal={openCompensationModal}
        setOpenModal={setOpenCompensationModal}
      />
      <BankOfHoursPanel
        selectedContext={tabFromNavigation}
        store={store}
        timeSheetData={timeSheetData}
      />
    </Sidepanel>
  )
}

export default observer(BankOfHoursBreakdownPanel)
